import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';

// Gentle fade and slide animations
const gentleSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gentleSlideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

const softPulse = keyframes`
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
`;

// Container for notifications
const NotificationContainer = styled.div`
  position: fixed;
  top: ${Spacing.xl}px;
  right: ${Spacing.xl}px;
  z-index: 9999;
  pointer-events: none;
  max-width: 420px;
`;

// Individual notification styling
const NotificationItemStyled = styled.div<{ 
  type: 'info' | 'success' | 'warning' | 'error';
  isExiting?: boolean;
}>`
  pointer-events: auto;
  margin-bottom: ${Spacing.md}px;
  padding: ${Spacing.lg}px ${Spacing.xl}px;
  background: ${Colors.surface};
  backdrop-filter: blur(20px);
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.md}px;
  position: relative;
  overflow: hidden;
  animation: ${props => props.isExiting ? gentleSlideOut : gentleSlideIn} 0.3s ease-out;
  border: 1px solid ${Colors.borderLight};
  
  // Gentle left border accent based on type
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${props => {
      switch (props.type) {
        case 'success': return Colors.success;
        case 'warning': return Colors.warning;
        case 'error': return Colors.danger;
        default: return Colors.primary;
      }
    }};
    opacity: 0.6;
  }

  // Subtle background tint
  background: ${props => {
    switch (props.type) {
      case 'success': return `linear-gradient(to right, ${Colors.success}08, ${Colors.surface})`;
      case 'warning': return `linear-gradient(to right, ${Colors.warning}08, ${Colors.surface})`;
      case 'error': return `linear-gradient(to right, ${Colors.danger}08, ${Colors.surface})`;
      default: return `linear-gradient(to right, ${Colors.primary}08, ${Colors.surface})`;
    }
  }};

  &:hover {
    box-shadow: ${Shadows.md};
    transform: translateX(-2px);
    transition: all 0.2s ease;
  }
`;

const NotificationIcon = styled.div<{ type: 'info' | 'success' | 'warning' | 'error' }>`
  font-size: ${FontSizes.lg}px;
  line-height: 1;
  opacity: 0.8;
  animation: ${softPulse} 2s ease-in-out infinite;
  color: ${props => {
    switch (props.type) {
      case 'success': return Colors.success;
      case 'warning': return Colors.warning;
      case 'error': return Colors.danger;
      default: return Colors.primary;
    }
  }};
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
  opacity: 0.95;
`;

const NotificationMessage = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  line-height: 1.4;
  opacity: 0.85;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${Colors.textTertiary};
  cursor: pointer;
  padding: ${Spacing.xs}px;
  font-size: ${FontSizes.md}px;
  opacity: 0.5;
  transition: opacity 0.2s ease;
  line-height: 1;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ProgressBar = styled.div<{ duration: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: ${Colors.primary}20;
  animation: progress ${props => props.duration}ms linear;
  transform-origin: left;
  
  @keyframes progress {
    from {
      width: 100%;
    }
    to {
      width: 0%;
    }
  }
`;

// Notification data type
export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  message: string;
  duration?: number;
  persistent?: boolean;
}

interface NotificationSystemProps {
  notifications: Notification[];
  onDismiss: (id: string) => void;
}

const NotificationSystem: React.FC<NotificationSystemProps> = ({ 
  notifications, 
  onDismiss 
}) => {
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

  const handleDismiss = (id: string) => {
    setExitingIds(prev => new Set(prev).add(id));
    setTimeout(() => {
      onDismiss(id);
      setExitingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 300);
  };

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success': return '✓';
      case 'warning': return '⚠';
      case 'error': return '✕';
      default: return 'ℹ';
    }
  };

  const getDefaultTitle = (type: Notification['type']) => {
    switch (type) {
      case 'success': return 'Success';
      case 'warning': return 'Warning';
      case 'error': return 'Error';
      default: return 'Info';
    }
  };

  return (
    <NotificationContainer>
      {notifications.map(notification => (
        <NotificationItemStyled
          key={notification.id}
          type={notification.type}
          isExiting={exitingIds.has(notification.id)}
        >
          <NotificationIcon type={notification.type}>
            {getIcon(notification.type)}
          </NotificationIcon>
          
          <NotificationContent>
            <NotificationTitle>
              {notification.title || getDefaultTitle(notification.type)}
            </NotificationTitle>
            <NotificationMessage>
              {notification.message}
            </NotificationMessage>
          </NotificationContent>
          
          <CloseButton onClick={() => handleDismiss(notification.id)}>
            ×
          </CloseButton>
          
          {!notification.persistent && notification.duration && (
            <ProgressBar duration={notification.duration} />
          )}
        </NotificationItemStyled>
      ))}
    </NotificationContainer>
  );
};

// Auto-dismiss component
export const NotificationItem: React.FC<{
  notification: Notification;
  onDismiss: (id: string) => void;
}> = ({ notification, onDismiss }) => {
  useEffect(() => {
    if (!notification.persistent && notification.duration) {
      const timer = setTimeout(() => {
        onDismiss(notification.id);
      }, notification.duration);
      
      return () => clearTimeout(timer);
    }
  }, [notification, onDismiss]);

  return null;
};

export default NotificationSystem;

// Gentle inline notification for forms
export const InlineNotification = styled.div<{ 
  type: 'info' | 'success' | 'warning' | 'error' 
}>`
  padding: ${Spacing.md}px ${Spacing.lg}px;
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.sm}px;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  margin: ${Spacing.md}px 0;
  opacity: 0.9;
  animation: ${gentleSlideIn} 0.3s ease-out;
  
  ${props => {
    const colors = {
      info: { bg: `${Colors.primary}10`, border: `${Colors.primary}30`, text: Colors.primary },
      success: { bg: `${Colors.success}10`, border: `${Colors.success}30`, text: Colors.success },
      warning: { bg: `${Colors.warning}10`, border: `${Colors.warning}30`, text: Colors.warning },
      error: { bg: `${Colors.danger}10`, border: `${Colors.danger}30`, text: Colors.danger }
    };
    
    const style = colors[props.type];
    return css`
      background: ${style.bg};
      border: 1px solid ${style.border};
      color: ${style.text};
    `;
  }}
`;